import { useEffect, useState } from 'react';

/* ==========================================================================
   One ticker for the whole console.

   Relative ages ("4s ago") and the header clock must all advance on the same
   beat, or two panels will disagree about what "now" is. Subscribers share a
   single interval; the last one to unmount stops it.
   ========================================================================== */

type Listener = (t: number) => void;

const listeners = new Set<Listener>();
let timer: ReturnType<typeof setInterval> | null = null;

function start() {
  if (timer !== null) return;
  timer = setInterval(() => {
    const t = Date.now();
    listeners.forEach((l) => l(t));
  }, 1000);
}

function stop() {
  if (timer === null || listeners.size > 0) return;
  clearInterval(timer);
  timer = null;
}

/**
 * Current epoch millis, re-rendering once a second.
 * `enabled: false` freezes the value — use it when a panel is scrubbed to
 * history, so a replayed age does not keep counting up as if it were live.
 */
export function useNow(enabled = true): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!enabled) return;
    const listener: Listener = (t) => setNow(t);
    listeners.add(listener);
    start();
    return () => {
      listeners.delete(listener);
      stop();
    };
  }, [enabled]);

  return now;
}
